import { useEffect, useState } from 'react';
import { IGcfProductListProduct } from 'src/interfaces/Product';

import { IProductVariationsAssets } from '../../MenuBoardModificationPage.interface';
import {
  applyImageVisibility,
  getDefaultImageFromAttributes,
  getProductImageFromConfig,
  IAttributeSize,
} from './ProductOverrideRightSideBar.helper';

export interface IUseProductOverrideSidebarParams {
  attributeSizes: IAttributeSize[];
  productConfig?: IGcfProductListProduct;
  mainProductAssetName?: string;
  selectProductKey: string;
  isProductFetching: boolean;
  productCode: string;
  productVariationsAssets?: IProductVariationsAssets;
}

const deriveDefaultFlavor = (
  sizes: IAttributeSize[],
  defaultSize: string | undefined,
): string | undefined => {
  if (defaultSize === undefined) {
    const firstSize = sizes.find((s) => s.flavors.some((f) => f.checked));

    return firstSize?.flavors.find((f) => f.images?.some((img) => img.visible))
      ?.productCode;
  }

  const targetSize = sizes.find((s) => s.name === defaultSize);

  return targetSize?.flavors.find((f) => f.images?.some((img) => img.visible))
    ?.productCode;
};

const deriveConfiguredVisibleFlavor = (
  productConfig: IGcfProductListProduct | undefined,
  defaultSize: string | undefined,
): string | undefined => {
  if (!defaultSize) {
    return undefined;
  }

  const targetSize = Object.values(productConfig?.sizes ?? {}).find(
    (size) => size.name === defaultSize,
  );

  return Object.values(targetSize?.variations ?? {}).find((variation) =>
    variation.config?.images?.some((image) => image.visible),
  )?.productCode;
};

const hydrateAttributeImagesFromVariationAssets = (
  sizes: IAttributeSize[],
  productVariationsAssets: IProductVariationsAssets = {},
): IAttributeSize[] => {
  let didHydrate = false;

  const hydratedSizes = sizes.map((size) => ({
    ...size,
    flavors: size.flavors.map((flavor) => {
      if (flavor.images?.some((img) => img.assetName)) {
        return flavor;
      }

      const variationAssets = productVariationsAssets[flavor.productCode] ?? [];
      if (variationAssets.length === 0) {
        return flavor;
      }

      didHydrate = true;
      const hasVisibleImage = flavor.images?.some((img) => img.visible);

      return {
        ...flavor,
        images: variationAssets.map((asset, index) => ({
          assetName: asset.name,
          visible: Boolean(hasVisibleImage && index === 0),
        })),
      };
    }),
  }));

  return didHydrate ? hydratedSizes : sizes;
};

export const useProductOverrideSidebar = ({
  attributeSizes,
  productConfig,
  mainProductAssetName,
  selectProductKey,
  isProductFetching,
  productCode,
  productVariationsAssets,
}: IUseProductOverrideSidebarParams) => {
  const [localAttributes, setLocalAttributes] = useState<IAttributeSize[]>([]);

  // Clear local state on product change so the sidebar shows a loading
  // indicator instead of the previous product's stale attributes.
  useEffect(() => {
    setLocalAttributes([]);
  }, [selectProductKey]);

  useEffect(() => {
    if (
      isProductFetching ||
      !selectProductKey ||
      !productCode ||
      attributeSizes.length === 0
    ) {
      return;
    }

    const appliedDefaultSize = productConfig?.config?.defaultSize;
    const configuredVisibleFlavor = deriveConfiguredVisibleFlavor(
      productConfig,
      appliedDefaultSize,
    );
    const productImage = getProductImageFromConfig(
      productConfig,
      mainProductAssetName,
    );

    const hydratedAttributes = hydrateAttributeImagesFromVariationAssets(
      attributeSizes,
      productVariationsAssets,
    );

    setLocalAttributes(
      productImage.visible
        ? applyImageVisibility(
            hydratedAttributes,
            appliedDefaultSize,
            configuredVisibleFlavor,
          )
        : hydratedAttributes,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectProductKey, productCode, isProductFetching]);

  useEffect(() => {
    if (isProductFetching) {
      return;
    }

    setLocalAttributes((current) =>
      hydrateAttributeImagesFromVariationAssets(
        current,
        productVariationsAssets,
      ),
    );
  }, [isProductFetching, productVariationsAssets]);

  const productImage = getProductImageFromConfig(
    productConfig,
    mainProductAssetName,
  );

  const configDefaultSize = productConfig?.config?.defaultSize;
  const defaultSize =
    configDefaultSize != null
      ? configDefaultSize
      : localAttributes.find((s) => s.flavors.some((f) => f.checked))?.name;

  const defaultFlavor = deriveDefaultFlavor(localAttributes, defaultSize);
  const showImage = productImage.visible;
  const defaultImage = getDefaultImageFromAttributes(
    localAttributes,
    defaultSize,
    productImage.assetName,
    defaultFlavor,
  );

  return {
    localAttributes,
    setLocalAttributes,
    defaultSize,
    defaultFlavor,
    showImage,
    defaultImage,
  };
};
